import { useMemo, useState } from "react";
import { TIER_COLORS } from "../utils/tiers";
import { getGroupSchedule, formatRange } from "../utils/time";
import { openingsByTier, totalSeats, totalGroups } from "../utils/openings";
import { spareSeats, waitlistCount, isWaiting } from "../utils/waitlist";
import { rankPlayersForGroup } from "../utils/playerFit";

function withAge(p) {
  return p.age != null ? `${p.name} (${p.age})` : p.name;
}

function whenLabel(g) {
  const { startTime, duration } = getGroupSchedule(g);
  return `${g.day} ${formatRange(startTime, duration)}`;
}

// Every clinic with room, level by level — the answer to "we have a Junior
// Intermediate on the phone, what can we offer them".
//
// Picking a member narrows to their level and nothing else: a coach placing
// someone wants to see every seat at the right standard, then judge the time
// themselves. Whether that member is actually free is still shown, per
// clinic, once one is open — it just doesn't hide anything.
export default function FindOpeningsView({
  players, groups, coaches = [], onPlayerClick, onJoinGroup, onWaitlistAdd, onNotify,
}) {
  const [memberId, setMemberId] = useState("");
  const [memberSearch, setMemberSearch] = useState("");
  const [openGroupId, setOpenGroupId] = useState(null);

  const member = players.find(p => p.id === memberId) || null;
  const coachMap = useMemo(
    () => Object.fromEntries(coaches.map(c => [c.id, c])),
    [coaches]
  );

  const sections = useMemo(
    () => openingsByTier(groups, { tier: member ? member.tier : null }),
    [groups, member]
  );

  const candidates = memberSearch.trim()
    ? players
        .filter(p => p.name.toLowerCase().includes(memberSearch.trim().toLowerCase()))
        .slice(0, 8)
    : [];

  const seats = totalSeats(sections);
  const clinics = totalGroups(sections);

  function pickMember(p) {
    setMemberId(p.id);
    setMemberSearch("");
    setOpenGroupId(null);
  }

  function clearMember() {
    setMemberId("");
    setMemberSearch("");
    setOpenGroupId(null);
  }

  return (
    <div className="p-4 max-w-3xl mx-auto space-y-5">
      <div>
        <h2 className="font-display text-xl text-brand-blue mb-1">Open Slots by Level</h2>
        <p className="text-sm text-gray-500">
          Every clinic on the calendar with room left, grouped by level. Weekend
          open play and privates aren't capped, so they're not listed here.
        </p>
      </div>

      {/* Member filter — level only */}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">
          Narrow to one member's level (optional)
        </label>
        {member ? (
          <div className="flex flex-wrap items-center gap-2">
            <span className="bg-blue-50 border border-blue-200 rounded-full pl-3 pr-2 py-1 text-xs flex items-center gap-2">
              <button onClick={() => onPlayerClick(member)} className="font-medium text-gray-800 hover:underline">
                {withAge(member)}
              </button>
              <span className="text-gray-500">{member.tier}</span>
              <button onClick={clearMember} className="text-gray-400 hover:text-gray-700 font-bold" title="Clear">
                ×
              </button>
            </span>
            <span className="text-xs text-gray-400">
              Showing their level only — times aren't filtered by their availability.
            </span>
          </div>
        ) : (
          <>
            <input
              type="text"
              placeholder="Search members by name…"
              value={memberSearch}
              onChange={e => setMemberSearch(e.target.value)}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            {candidates.length > 0 && (
              <div className="mt-1.5 space-y-1 max-h-48 overflow-y-auto border border-gray-100 rounded-lg p-1.5">
                {candidates.map(p => (
                  <button
                    key={p.id}
                    onClick={() => pickMember(p)}
                    className="w-full text-left rounded px-3 py-2 text-xs hover:bg-blue-50 border border-transparent hover:border-blue-200 transition-colors"
                  >
                    <span className="font-medium text-gray-800">{withAge(p)}</span>
                    <span className="text-gray-400 ml-2">{p.tier}</span>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <div className="text-xs text-gray-500">
        {seats} seat{seats === 1 ? "" : "s"} free across {clinics} clinic{clinics === 1 ? "" : "s"}
      </div>

      {/* One section per level, empty ones included: a level with nothing free
          is where the next group needs opening. */}
      <div className="space-y-4">
        {sections.map(section => (
          <TierSection
            key={section.tier}
            section={section}
            players={players}
            coachMap={coachMap}
            member={member}
            openGroupId={openGroupId}
            onToggle={id => setOpenGroupId(prev => (prev === id ? null : id))}
            onPlayerClick={onPlayerClick}
            onJoinGroup={onJoinGroup}
            onWaitlistAdd={onWaitlistAdd}
            onNotify={onNotify}
          />
        ))}
      </div>
    </div>
  );
}

function TierSection({
  section, players, coachMap, member, openGroupId, onToggle,
  onPlayerClick, onJoinGroup, onWaitlistAdd, onNotify,
}) {
  const c = TIER_COLORS[section.tier] || TIER_COLORS["No Group"];
  return (
    <div className={`${c.border} border rounded-lg overflow-hidden`}>
      <div className={`${c.header} px-3 py-2 flex flex-wrap items-center justify-between gap-2`}>
        <span className={`font-semibold text-sm ${c.text}`}>{section.tier}</span>
        <span className={`text-xs ${c.text}`}>
          {section.groups.length === 0
            ? "full"
            : `${section.seats} seat${section.seats === 1 ? "" : "s"} · ${section.groups.length} clinic${section.groups.length === 1 ? "" : "s"}`}
          {section.waiting > 0 ? ` · ${section.waiting} waiting` : ""}
        </span>
      </div>

      <div className={`${c.bg} p-2 space-y-2`}>
        {section.groups.length === 0 ? (
          <p className="text-xs text-gray-500 italic px-1 py-1">
            No room at this level — every clinic is at 4/4.
          </p>
        ) : (
          section.groups.map(g => (
            <OpeningRow
              key={g.id}
              group={g}
              players={players}
              coach={coachMap[g.coachId]}
              member={member}
              expanded={openGroupId === g.id}
              onToggle={() => onToggle(g.id)}
              onPlayerClick={onPlayerClick}
              onJoinGroup={onJoinGroup}
              onWaitlistAdd={onWaitlistAdd}
              onNotify={onNotify}
            />
          ))
        )}
      </div>
    </div>
  );
}

function OpeningRow({
  group, players, coach, member, expanded, onToggle,
  onPlayerClick, onJoinGroup, onWaitlistAdd, onNotify,
}) {
  const spare = spareSeats(group);
  const waiting = waitlistCount(group);

  return (
    <div className="bg-white border border-gray-200 rounded-lg">
      <button onClick={onToggle} className="w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors rounded-lg">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-mono text-xs font-bold text-gray-700">{group.id}</span>
            <span className="text-sm text-gray-800 truncate">{whenLabel(group)}</span>
          </div>
          <div className="flex items-center gap-1.5">
            {waiting > 0 && (
              <span
                className="text-xs px-2 py-0.5 rounded font-medium bg-amber-100 text-amber-800"
                title="On the waitlist for this clinic"
              >
                {waiting} waiting
              </span>
            )}
            <span className="text-xs px-2 py-0.5 rounded font-semibold bg-green-100 text-green-800">
              {spare} seat{spare === 1 ? "" : "s"} free
            </span>
            <span className="text-xs text-gray-400">{group.playerIds.length}/4</span>
          </div>
        </div>
        {/* Court and coach on one line, the roster on the next. Run together,
            the coach's name read as just another player — so it carries their
            color dot here, the same tag the Schedule list and grid give them. */}
        <div className="text-xs text-gray-500 mt-0.5 flex flex-wrap items-center gap-x-1.5">
          <span>{group.court}</span>
          <span className="text-gray-300">·</span>
          {coach ? (
            <span className="inline-flex items-center gap-1" title={`Coach: ${coach.name}`}>
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: coach.color || "#64748b" }}
              />
              {coach.name}
              <span className="text-gray-400">(coach)</span>
            </span>
          ) : (
            <span className="italic text-gray-400">No coach</span>
          )}
        </div>
        <div className="text-xs text-gray-500">
          {group.playerIds.length > 0 ? (
            <>
              <span className="text-gray-400">In this clinic: </span>
              {group.playerIds.map(id => players.find(p => p.id === id)?.name).filter(Boolean).join(", ")}
            </>
          ) : (
            <span className="italic text-gray-400">Nobody placed yet</span>
          )}
        </div>
      </button>

      {expanded && (
        <PlacePanel
          group={group}
          players={players}
          member={member}
          onPlayerClick={onPlayerClick}
          onJoinGroup={onJoinGroup}
          onWaitlistAdd={onWaitlistAdd}
          onNotify={onNotify}
        />
      )}
    </div>
  );
}

// The whole roster ranked by fit, same as Find by player — nobody is hidden,
// the warnings just say why someone is a poor match. When a member is
// selected upstairs they're pinned to the top, since they're who this seat is
// being looked at for.
function PlacePanel({ group, players, member, onPlayerClick, onJoinGroup, onWaitlistAdd, onNotify }) {
  const [search, setSearch] = useState("");

  const ranked = useMemo(
    () => rankPlayersForGroup(group, players),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [group, players]
  );

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    const matching = term
      ? ranked.filter(r => r.player.name.toLowerCase().includes(term))
      : ranked;
    if (!member) return matching.slice(0, 12);
    const pinned = matching.filter(r => r.player.id === member.id);
    return [...pinned, ...matching.filter(r => r.player.id !== member.id)].slice(0, 12);
  }, [ranked, search, member]);

  function add(player) {
    onJoinGroup(group.id, [player.id]);
    onNotify?.({ message: `Added ${player.name} to ${group.id}` });
  }

  function queue(player) {
    onWaitlistAdd(group.id, player.id);
    onNotify?.({ message: `${player.name} added to the ${group.id} waitlist` });
  }

  return (
    <div className="border-t border-gray-100 px-3 py-2.5 space-y-2">
      <input
        type="text"
        placeholder="Search members to place…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
      />

      {visible.map(r => (
        <CandidateRow
          key={r.player.id}
          result={r}
          group={group}
          highlighted={member?.id === r.player.id}
          onAdd={add}
          onQueue={queue}
          onPlayerClick={onPlayerClick}
        />
      ))}
      {visible.length === 0 && (
        <p className="text-xs text-gray-400 italic">No members match that name.</p>
      )}
      {!search.trim() && (
        <p className="text-xs text-gray-400">Best fits first — search to find anyone else.</p>
      )}
    </div>
  );
}

function CandidateRow({ result, group, highlighted, onAdd, onQueue, onPlayerClick }) {
  const { player, score, available, inGroup, reasons } = result;
  const c = TIER_COLORS[player.tier] || TIER_COLORS["No Group"];
  const queued = isWaiting(group, player.id);
  const negatives = reasons.filter(rn => rn.kind === "neg");

  return (
    <div className={`border rounded-lg p-2 ${highlighted ? "border-blue-300 bg-blue-50" : inGroup ? "border-gray-200 bg-gray-50 opacity-75" : "border-gray-200 bg-white"}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={() => onPlayerClick(player)} className="font-medium text-sm text-gray-800 hover:underline truncate">
            {withAge(player)}
          </button>
          <span className={`${c.bg} ${c.border} border text-xs px-1.5 py-0.5 rounded ${c.text} whitespace-nowrap`}>{player.tier}</span>
          {available === false && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">Not free then</span>
          )}
        </div>
        <span className="text-xs px-2 py-0.5 rounded font-semibold bg-blue-100 text-blue-800" title="Fit score">fit {score}</span>
      </div>

      {negatives.length > 0 && (
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
          {negatives.map((rn, i) => (
            <span key={i} className="text-xs text-red-600">⚠ {rn.text}</span>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-1.5 mt-1.5">
        {inGroup ? (
          <span className="text-xs text-gray-500 italic py-1">Already in this clinic</span>
        ) : (
          <>
            <button
              onClick={() => onAdd(player)}
              className="text-xs bg-blue-600 hover:bg-blue-700 text-white rounded px-2.5 py-1 font-medium transition-colors"
            >
              + Place here
            </button>
            <button
              onClick={() => onQueue(player)}
              disabled={queued}
              className="text-xs border border-gray-300 hover:border-gray-400 disabled:opacity-40 disabled:cursor-not-allowed text-gray-600 hover:text-gray-800 rounded px-2.5 py-1 font-medium transition-colors"
              title={queued ? "Already on this waitlist" : "Add to the waitlist instead of placing them"}
            >
              {queued ? "On waitlist" : "Waitlist"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
